import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useCreateTagProposal } from '@/hooks/useTagProposals';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';

const PREDEFINED_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', 
  '#EC4899', '#14B8A6', '#F97316', '#84CC16', '#6366F1'
];

export const ProposeTagDialog = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState(PREDEFINED_COLORS[0]);
  
  const createProposal = useCreateTagProposal();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) return;

    createProposal.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
      color: selectedColor,
    }, {
      onSuccess: () => {
        setOpen(false);
        setName('');
        setDescription('');
        setSelectedColor(PREDEFINED_COLORS[0]);
      }
    });
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="border-dashed border-gaming-primary text-gaming-primary hover:bg-gaming-primary/10"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('tags.proposeTag')}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-700 p-4 sm:p-6">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-white text-lg sm:text-xl">{t('proposeTag.title')}</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              {t('proposeTag.description')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-white mb-2 block">
                {t('proposeTag.nameLabel')}
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('proposeTag.namePlaceholder')}
                className="bg-slate-800 border-slate-600 text-white"
                maxLength={30}
                required
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-white mb-2 block">
                {t('proposeTag.descriptionLabel')}
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('proposeTag.descriptionPlaceholder')}
                className="bg-slate-800 border-slate-600 text-white"
                maxLength={200}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-white mb-2 block">
                {t('proposeTag.colorLabel')}
              </label>
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-2">
                {PREDEFINED_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 min-w-[40px] min-h-[40px] sm:min-w-[48px] sm:min-h-[48px] ${
                      selectedColor === color ? 'border-white' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setSelectedColor(color)}
                  />
                ))}
              </div>
              
              {name && (
                <div className="mt-3">
                  <p className="text-sm text-slate-400 mb-2">{t('proposeTag.preview')} :</p>
                  <Badge 
                    className="text-white border"
                    style={{ backgroundColor: selectedColor + '33', borderColor: selectedColor + '66' }}
                  >
                    <div 
                      className="w-2 h-2 rounded-full mr-2" 
                      style={{ backgroundColor: selectedColor }}
                    />
                    {name}
                  </Badge>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-white w-full sm:w-auto min-h-[44px]"
            >
              {t('button.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || createProposal.isPending}
              className="bg-gaming-primary hover:bg-gaming-primary/80 w-full sm:w-auto min-h-[44px]"
            >
              {createProposal.isPending ? t('proposeTag.submitting') : t('proposeTag.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};